import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { RootState } from '../../store/store';
import { queryParams } from '../../utils/query-string';
import { IListResponse } from '../../configs/constants';
import { IParams, INew } from './news';
import { convertModelToFormData } from '../../utils/utility';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 2 });

export const newApi = createApi({
  reducerPath: 'newApi',
  baseQuery: baseQueryWithRetry,
  tagTypes: ['New'],
  endpoints: (builder) => ({
    getNews: builder.query<
      IListResponse<INew>,
      { page: number; pageSize: number; status?: string }
    >({
      query: ({ page, pageSize, status }) => {
        const params: IParams = {
          take: pageSize,
          skip: (page - 1) * pageSize,
        };
        if (status) params.status = status;
        return {
          url: `news?${queryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: ['New'],
    }),
    addNew: builder.mutation<{ data: INew; type: string }, any>({
      query: (body) => {
        return {
          url: 'news',
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['New'],
    }),
    updateNew: builder.mutation<{ data: INew; type: string }, any>({
      query: (body) => {
        const { id } = body;
        delete body.id;
        return {
          url: `news/${id}`,
          method: 'PATCH',
          body: convertModelToFormData(body),
        };
      },
      invalidatesTags: ['New'],
    }),
    deleteNew: builder.mutation<{ data: string; type: string }, any>({
      query: (id) => {
        return {
          url: `news/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['New'],
    }),
  }),
});

export const {
  endpoints: { getNews },
} = newApi;
export const { useGetNewsQuery, useAddNewMutation, useUpdateNewMutation, useDeleteNewMutation } =
  newApi;
